import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, IconButton, Avatar, Menu, MenuItem, Divider,
  useMediaQuery, Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import GavelIcon from "@mui/icons-material/Gavel";
import CelebrationIcon from "@mui/icons-material/Celebration";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import SettingsIcon from "@mui/icons-material/Settings";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import { getUsuario, logout, refreshUsuario } from "../services/authService";
import { APP_NOME, APP_SUBTITULO } from "../config/appConfig";

const LARGURA = 248;
const NAV = [
  { to: "/fichas", label: "Fichas", icon: AssignmentIcon },
  { to: "/recursos", label: "Recursos", icon: GavelIcon },
  { to: "/eventos-admin", label: "Eventos", icon: CelebrationIcon },
  { to: "/inscritos", label: "Inscritos", icon: HowToRegIcon },
  { to: "/formularios", label: "Formulários", icon: DescriptionIcon },
  { to: "/edicoes", label: "Edições", icon: EventIcon },
  { to: "/usuarios", label: "Usuários", icon: PeopleIcon },
  { to: "/configuracoes", label: "Configurações", icon: SettingsIcon },
];

function iniciais(nome) {
  if (!nome) return "?";
  const p = nome.trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[p.length - 1]?.[0] || "")).toUpperCase();
}

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuEl, setMenuEl] = useState(null);
  const usuario = getUsuario();

  // Atualiza dados do usuário (ex.: flag Admin) ao abrir o painel.
  useEffect(() => { refreshUsuario(); }, []);

  const ativo = (to) => location.pathname.startsWith(to);
  const tituloPagina = NAV.find((n) => ativo(n.to))?.label || APP_NOME;

  const conteudoDrawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#0f1b3d", color: "#fff" }}>
      <Box sx={{ px: 2.5, py: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar sx={{ bgcolor: "primary.main", width: 38, height: 38 }}>
          <VolunteerActivismIcon fontSize="small" />
        </Avatar>
        <Box>
          <Typography variant="subtitle1" sx={{ lineHeight: 1.1 }}>{APP_NOME}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>{APP_SUBTITULO}</Typography>
        </Box>
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      <List sx={{ flexGrow: 1, py: 1 }}>
        {NAV.map((n) => {
          const Icone = n.icon;
          const sel = ativo(n.to);
          return (
            <ListItem key={n.to} disablePadding>
              <ListItemButton
                selected={sel}
                onClick={() => { navigate(n.to); setMobileOpen(false); }}
                sx={{
                  color: sel ? "#fff" : "rgba(255,255,255,0.75)",
                  "&.Mui-selected": { backgroundColor: "rgba(92,124,250,0.25)" },
                  "&.Mui-selected:hover": { backgroundColor: "rgba(92,124,250,0.32)" },
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
                }}
              >
                <ListItemIcon sx={{ color: "inherit", minWidth: 38 }}><Icone fontSize="small" /></ListItemIcon>
                <ListItemText primary={n.label} primaryTypographyProps={{ fontWeight: sel ? 700 : 500 }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>v1.0 · EVENTOSFACDO</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: LARGURA }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={desktop ? "permanent" : "temporary"}
          open={desktop ? true : mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ "& .MuiDrawer-paper": { width: LARGURA, boxSizing: "border-box", border: "none" } }}
        >
          {conteudoDrawer}
        </Drawer>
      </Box>

      {/* Área principal */}
      <Box sx={{ flexGrow: 1, width: { md: `calc(100% - ${LARGURA}px)` }, display: "flex", flexDirection: "column" }}>
        <AppBar position="sticky" elevation={0} sx={{ bgcolor: "background.paper", color: "text.primary", borderBottom: "1px solid", borderColor: "divider" }}>
          <Toolbar>
            {!desktop && (
              <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" sx={{ flexGrow: 1 }}>{tituloPagina}</Typography>
            <Tooltip title={usuario?.Nome || ""}>
              <IconButton onClick={(e) => setMenuEl(e.currentTarget)} size="small">
                <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.main", fontSize: 14 }}>
                  {iniciais(usuario?.Nome)}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)}>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2">{usuario?.Nome}</Typography>
                <Typography variant="caption" color="text.secondary">{usuario?.Email}</Typography>
              </Box>
              <Divider />
              <MenuItem onClick={logout}><ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>Sair</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Box component="main" sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
